import React, { useEffect } from "react";
import { X, Crown, Medal, Trophy, Target, CheckCircle2, XCircle } from "lucide-react";
import { useI18n } from "../i18n";

const reasonLabel = (reason, t) => {
  if (reason === "exact") return t("exact");
  if (reason === "outcome") return t("outcome");
  return t("miss");
};

const podiumColors = ["#FFB703", "#C0C0C0", "#CD7F32"];

export default function MatchResultAlert({ result, onClose }) {
  const { t } = useI18n();

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!result) return null;
  const { match, awards = [], leaderboard = [] } = result;

  const exactWinners = awards.filter((a) => a.reason === "exact");
  const outcomeWinners = awards.filter((a) => a.reason === "outcome");
  const missed = awards.filter((a) => a.reason === "none");

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
      data-testid="match-result-alert"
    >
      <div
        className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto border border-[#00D37F]/30 bg-[#0d0d0d] shadow-[0_0_60px_rgba(0,211,127,0.2)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header / Scoreboard */}
        <div className="scoreboard relative px-6 py-5 border-b border-white/10">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center bg-white/5 hover:bg-white/15 border border-white/10 text-white/70 hover:text-white"
            data-testid="alert-close"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="text-[10px] uppercase tracking-[0.3em] font-mono font-bold text-[#00D37F] mb-2">
            {t("ft")} · {t("result_saved").toUpperCase()}
          </div>
          <div className="font-mono text-[11px] text-white/40 mb-3">{match.stage}</div>
          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <span className="text-4xl">{match.home_flag}</span>
              <span className="font-head text-xl truncate">{match.home_team}</span>
            </div>
            <div className="flex items-center gap-3 font-mono font-bold text-5xl tabular-nums text-[#00D37F]">
              <span>{match.home_score}</span>
              <span className="text-white/20">:</span>
              <span>{match.away_score}</span>
            </div>
            <div className="flex items-center gap-3 justify-end min-w-0">
              <span className="font-head text-xl text-right truncate">{match.away_team}</span>
              <span className="text-4xl">{match.away_flag}</span>
            </div>
          </div>
        </div>

        {/* Awards summary */}
        <div className="px-6 py-5 border-b border-white/10">
          <div className="text-[11px] uppercase tracking-[0.3em] font-mono font-bold text-white/50 mb-4">
            {t("awards_summary")}
          </div>

          <div className="grid grid-cols-3 gap-3 mb-5">
            <div className="border border-[#00D37F]/30 bg-[#00D37F]/10 p-3">
              <div className="text-[10px] uppercase tracking-[0.25em] font-mono font-bold text-[#00D37F]">
                {t("exact")}
              </div>
              <div className="font-display text-3xl text-[#00D37F] mt-1">{exactWinners.length}</div>
              <div className="text-[10px] font-mono text-white/40">+5 PTS</div>
            </div>
            <div className="border border-[#FFB703]/30 bg-[#FFB703]/10 p-3">
              <div className="text-[10px] uppercase tracking-[0.25em] font-mono font-bold text-[#FFB703]">
                {t("outcome")}
              </div>
              <div className="font-display text-3xl text-[#FFB703] mt-1">{outcomeWinners.length}</div>
              <div className="text-[10px] font-mono text-white/40">+2 PTS</div>
            </div>
            <div className="border border-white/15 bg-white/[0.03] p-3">
              <div className="text-[10px] uppercase tracking-[0.25em] font-mono font-bold text-white/60">
                {t("miss")}
              </div>
              <div className="font-display text-3xl text-white/40 mt-1">{missed.length}</div>
              <div className="text-[10px] font-mono text-white/40">+0 PTS</div>
            </div>
          </div>

          {awards.length === 0 ? (
            <div className="text-xs text-white/40 font-mono">{t("no_predictions_match")}</div>
          ) : (
            <div className="border border-white/10 bg-black/40 max-h-56 overflow-y-auto" data-testid="alert-awards">
              {awards.map((a) => (
                <div
                  key={a.user_id + a.prediction}
                  className="grid grid-cols-[1fr_auto_auto] items-center gap-3 px-3 py-2 border-b border-white/5 text-sm"
                >
                  <span className="flex items-center gap-2 min-w-0">
                    {a.reason === "exact" ? (
                      <Target className="w-3.5 h-3.5 text-[#00D37F] shrink-0" />
                    ) : a.reason === "outcome" ? (
                      <CheckCircle2 className="w-3.5 h-3.5 text-[#FFB703] shrink-0" />
                    ) : (
                      <XCircle className="w-3.5 h-3.5 text-white/30 shrink-0" />
                    )}
                    <span className="font-head text-xs truncate">@{a.username}</span>
                    {a.name && (
                      <span className="text-[10px] text-white/40 font-mono truncate hidden sm:inline">
                        · {a.name}
                      </span>
                    )}
                  </span>
                  <span className="font-mono text-xs text-white/70 tabular-nums">{a.prediction}</span>
                  <span
                    className={`font-mono text-xs font-bold tabular-nums px-2 py-0.5 border ${
                      a.awarded >= 5
                        ? "text-[#00D37F] bg-[#00D37F]/10 border-[#00D37F]/40"
                        : a.awarded > 0
                        ? "text-[#FFB703] bg-[#FFB703]/10 border-[#FFB703]/40"
                        : "text-white/40 bg-white/5 border-white/10"
                    }`}
                  >
                    {a.awarded > 0 ? "+" : ""}
                    {a.awarded}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Live leaderboard */}
        <div className="px-6 py-5">
          <div className="flex items-center gap-2 mb-3">
            <Trophy className="w-4 h-4 text-[#00D37F]" />
            <span className="text-[11px] uppercase tracking-[0.3em] font-mono font-bold text-[#00D37F]">
              {t("live_board")}
            </span>
          </div>

          <div className="border border-white/10 bg-black/40 max-h-72 overflow-y-auto" data-testid="alert-leaderboard">
            {leaderboard.length === 0 ? (
              <div className="px-4 py-3 text-xs text-white/40 font-mono">{t("no_players")}</div>
            ) : (
              leaderboard.map((u) => {
                const podium = u.rank <= 3;
                const color = podium ? podiumColors[u.rank - 1] : "#FFFFFF";
                return (
                  <div
                    key={u.id}
                    className="grid grid-cols-[60px_1fr_80px] items-center px-4 py-2.5 border-b border-white/5"
                    data-testid={`alert-leader-${u.id}`}
                  >
                    <span className="flex items-center gap-1">
                      {u.rank === 1 ? (
                        <Crown className="w-3.5 h-3.5" style={{ color }} />
                      ) : podium ? (
                        <Medal className="w-3.5 h-3.5" style={{ color }} />
                      ) : null}
                      <span className="font-display text-base leading-none" style={{ color }}>
                        #{u.rank}
                      </span>
                    </span>
                    <span className="font-head text-sm truncate">
                      @{u.username}
                      <span className="text-white/40 text-[10px] font-mono ml-2">{u.name}</span>
                    </span>
                    <span
                      className="font-mono text-base font-bold text-right tabular-nums"
                      style={{ color: podium ? color : "#00D37F" }}
                    >
                      {u.points ?? 0}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="px-6 pb-5">
          <button
            onClick={onClose}
            data-testid="alert-dismiss"
            className="w-full h-12 bg-[#00D37F] hover:bg-[#00E78B] text-black font-head text-sm transition-colors"
          >
            {t("close")}
          </button>
        </div>
      </div>
    </div>
  );
}
